import { useMemo, useState, Fragment } from 'react';
import { ScrollX } from './ScrollX.jsx';
import { buildTableData, NO_TABLE_CHECKS } from '../../pdf/helpers/activity-table-column-schema.js';
import { xlsxFilenameFor } from '../../shared/checkExport.js';
import { ACTIVITY_PREVIEW_ROWS, ACTIVITY_EXPAND_ROWS } from '../../shared/activityPreview.js';

// Dashboard expand cap. Beyond this, the per-check XLSX is the complete-list
// sink (the full list never renders in the browser — scale-safe). The preview
// count (ACTIVITY_PREVIEW_ROWS) is shared with the PDF for Rule-12 coherence;
// the expand cap is shared with the dashboard.json projection (Stage 5.2) so the
// projected array always holds enough rows for the table to expand fully.
const EXPAND_ROWS = ACTIVITY_EXPAND_ROWS;

function formatCell(col, value) {
  if (value == null || value === '') return '—';
  if (col.kind === 'float') {
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return String(Math.round(num));
  }
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(1);
  return String(value);
}

// X-2: arrow-joined path cells (col.wrapOn, e.g. #6 loop path / #5 implied path)
// render each token in a nowrap span so line breaks fall only on the " → "
// separator, never inside a hyphenated activity ID. Every other cell renders its
// formatted string unchanged.
function renderCellContent(col, value) {
  const text = formatCell(col, value);
  if (col.wrapOn && typeof text === 'string' && text.includes(col.wrapOn)) {
    const parts = text.split(col.wrapOn);
    return parts.map((tok, i) => (
      <Fragment key={i}>
        {i > 0 ? col.wrapOn : null}
        <span style={{ whiteSpace: 'nowrap' }}>{tok}</span>
      </Fragment>
    ));
  }
  return text;
}

function floatCellClass(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 'float-ok';
  if (num < 0) return 'float-neg';
  if (num === 0) return 'float-zero';
  return 'float-ok';
}

function formatFloatDays(value) {
  if (value == null) return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return `${Math.round(num)}d`;
}

/**
 * Activity detail table. Imports the column schema from the PDF helpers so
 * dashboard rows stay bit-for-bit identical to the rows emitted to XLSX (and,
 * by extension, the PDF table). The findings array arrives pre-sorted by impact
 * (§6) from the engine, so the preview is the worst-N, not file order.
 *
 * Scale-safe truncation: shows the top {ACTIVITY_PREVIEW_ROWS}; >12 reveals a
 * "Show all" toggle that expands to at most {EXPAND_ROWS}; beyond that, the
 * per-check XLSX holds the complete list. Pass `checkNumber` and `findings`.
 */
export function ActivityTable({
  checkNumber,
  checkName,
  findings = [],
  xlsxDownloadHref,
  totalCount,
  emptyMessage = 'No findings for this check.',
}) {
  const [expanded, setExpanded] = useState(false);
  const tableData = useMemo(() => {
    if (NO_TABLE_CHECKS.has(checkNumber)) return null;
    return buildTableData(checkNumber, findings);
  }, [checkNumber, findings]);

  if (!tableData) return null;
  const { columns, rows } = tableData;
  if (rows.length === 0) {
    return <p className="table-empty">{emptyMessage}</p>;
  }

  // True count of affected activities. The dashboard.json projection caps the
  // findings array (scale-safe), so its length is NOT the real total — the caller
  // passes the true count via totalCount. Falls back to rows.length for callers
  // that still hand over the full array (e.g. legacy analysis.json reads).
  const total = Number.isFinite(totalCount) ? totalCount : rows.length;
  const canExpand = total > ACTIVITY_PREVIEW_ROWS;
  const cap = expanded ? EXPAND_ROWS : ACTIVITY_PREVIEW_ROWS;
  const shown = Math.min(total, cap);
  const cappedByExpand = expanded && total > EXPAND_ROWS; // more than the 50 cap remains
  const xlsxName = xlsxFilenameFor(checkNumber, checkName);

  const header = (
    <tr>
      {columns.map((col) => (
        <th key={col.key} style={{ textAlign: col.align === 'right' ? 'right' : 'left' }}>
          {col.label}
        </th>
      ))}
    </tr>
  );

  // 10D-2/Sub-2: Check #36 row borders. Each finding carries float_position;
  // the first cell gets a class so the left edge renders critical/near/floating
  // colored. Other checks emit findings without float_position and the class
  // is omitted (backward-compatible).
  const floatPositionFor = (rowIndex) => {
    const f = findings[rowIndex];
    return (f && f.float_position) ? f.float_position : null;
  };

  const renderRow = (rowIndex) => {
    const row = rows[rowIndex];
    const fp = floatPositionFor(rowIndex);
    return columns.map((col, ci) => {
      const raw = row[ci];
      const isFloat = col.key === 'totalFloatDays';
      const firstCellFpClass = ci === 0 && fp ? ` act-fp act-fp-${fp.replace(/_/g, '-')}` : '';
      if (isFloat) {
        const cls = floatCellClass(raw);
        return (
          <td key={col.key} className={firstCellFpClass.trim() || undefined} style={{ textAlign: 'right' }}>
            <span className={cls}>{formatFloatDays(raw)}</span>
          </td>
        );
      }
      const baseClass = col.key === 'activityId' ? 'act-id' : '';
      const className = (baseClass + firstCellFpClass).trim() || undefined;
      return (
        <td
          key={col.key}
          className={className}
          style={{
            textAlign: col.align === 'right' ? 'right' : 'left',
            whiteSpace: col.nowrap ? 'nowrap' : undefined,
          }}
        >
          {renderCellContent(col, raw)}
        </td>
      );
    });
  };

  return (
    <div className="act-table-wrap">
      {/* MOBILE-FIT M2: the wide table slides; the cap note + footer stay put. */}
      <ScrollX>
        <table className="act-table">
          <thead>{header}</thead>
          <tbody>
            {Array.from({ length: shown }, (_, ri) => (
              <tr key={ri}>{renderRow(ri)}</tr>
            ))}
          </tbody>
        </table>
      </ScrollX>

      {cappedByExpand && (
        <p className="act-table-cap-note">
          Showing {EXPAND_ROWS} of {total} — complete list in this check’s spreadsheet download ({xlsxName}).
        </p>
      )}

      <div className="table-footer">
        <span className="table-footer-text">
          Showing {shown} of {total} {total === 1 ? 'activity' : 'activities'}
        </span>
        <div className="table-btns">
          {canExpand && (
            <button
              type="button"
              className="btn-text act-table-toggle"
              onClick={() => setExpanded((e) => !e)}
              aria-expanded={expanded}
            >
              {expanded ? 'Show fewer' : `Show all ${total} activities`}
            </button>
          )}
          {xlsxDownloadHref && (
            <a
              className="btn-filled"
              href={xlsxDownloadHref}
              download={xlsxName}
            >
              <span aria-hidden="true">↓</span> Export XLSX
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
